import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import moment from "moment";

export interface FetchQuotationsParams {
  page: number;
  pageSize: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any[];
}

// Fetch quotations list
export const fetchQuotations = createAsyncThunk(
  "quotation/fetchQuotations",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (
            filterField &&
            filterValue !== undefined &&
            filterValue !== null
          ) {
            if (typeof filterValue === "object" && filterValue !== null) {
              // Handle cases like { from_date: ..., to_date: ... }
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
                if (
                  (filterValue.from_date || filterValue.to_date) &&
                  (filterField === "created_at" || filterField === "updated_at")
                ) {
                  if (filterField === "created_at") {
                    filterField = "created";
                  } else {
                    filterField = "updated";
                  }
                  params.append("filter_by", filterField);
                }
              });
            } else {
              // Handle primitive values (string, number)
              params.append(filterField, filterValue);
            }
          }
        }
      );
    }
    const response: any = await Axios.get(`/quotation/?${params.toString()}`);

    return {
      data: response.data.data || response.data || [],
      rowCount: response.data.count || response.count || 0,
    };
  }
);

// Fetch quotation by ID
export const fetchQuotationById = createAsyncThunk(
  "quotation/fetchQuotationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/quotation/${id}/get`);
      // Handle response structure: response.data.data or response.data
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch quotation"
      );
    }
  }
);

// Create quotation
export const createQuotation = createAsyncThunk(
  "quotation/createQuotation",
  async (payload: any, { rejectWithValue }) => {
    try {
      // Check if payload has files (FormData)
      const hasFiles = payload instanceof FormData;

      if (hasFiles) {
        const response = await Axios.post("/quotation/create/", payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        return response.data?.data || response.data;
      } else {
        const response = await Axios.post("/quotation/create/", payload);
        return response.data?.data || response.data;
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to create quotation"
      );
    }
  }
);

// Update quotation
export const updateQuotation = createAsyncThunk(
  "quotation/updateQuotation",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      // Check if payload has files (FormData)
      const hasFiles = payload instanceof FormData;

      if (hasFiles) {
        const response = await Axios.put(`/quotation/${id}/update/`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        return response.data?.data || response.data;
      } else {
        const response = await Axios.put(`/quotation/${id}/update/`, payload);
        return response.data?.data || response.data;
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to update quotation"
      );
    }
  }
);

// Delete quotation
export const deleteQuotation = createAsyncThunk(
  "quotation/deleteQuotation",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/quotation/${id}/delete/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to delete quotation"
      );
    }
  }
);

// Download quotation PDF
export const downloadQuotationPdf = createAsyncThunk(
  "quotation/downloadQuotationPdf",
  async (
    {
      id,
      quotationNumber,
    }: {
      id: string;
      quotationNumber?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(`/quotation/${id}/pdf`, {
        responseType: "blob",
      });

      const disposition =
        response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"];
      const matches = disposition?.match(/filename="?([^"]+)"?/i);
      const fileName =
        matches?.[1] || `quotation-${quotationNumber || id}.pdf`;

      return { blob: response.data as Blob, filename: fileName };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to download quotation PDF"
      );
    }
  }
);

// Update quotation status
export const updateQuotationStatus = createAsyncThunk(
  "quotation/updateQuotationStatus",
  async ({ id, stage }: { id: string; stage: string }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/quotation/${id}/stage/`, { stage });
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          error.message ||
          "Failed to update quotation status"
      );
    }
  }
);

// Export quotations CSV
export const exportQuotationsCSV = async ({
  sort,
  order,
  search,
  filters,
}: {
  sort: any;
  order: any;
  search: any;
  filters: any;
}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }
  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            // Handle cases like { from_date: ..., to_date: ... }
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            // Handle primitive values (string, number)
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }
  try {
    const response = await Axios.get(
      `${api?.quotation?.export}?${params.toString()}`,
      {
        params: { export: "csv" },
        responseType: "blob", // important
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `quotations_${moment().format("YYYY-MM-DD")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading CSV", error);
  }
};

// Fetch dropdown options for quotation filters
export const fetchQuotationDropdowns = createAsyncThunk(
  "quotation/fetchDropdowns",
  async (columnId: string, { rejectWithValue }) => {
    try {
      let url = "";

      switch (columnId) {
        case "customer_name":
          url = "/quotation/dropdown/customer_name";
          break;
        case "quotation_no":
          url = "/quotation/dropdown/quotation_number";
          break;
        case "enquiry_no":
          url = "/quotation/dropdown/enquiry_number";
          break;
        case "stage":
          url = "/quotation/dropdown/stage";
          break;
        case "status":
          url = "/quotation/dropdown/stage";
          break;
        default:
          url = `/quotation/dropdown/${columnId}`;
      }

      const response = await Axios.get(url);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.[columnId] || [];

      return { columnId, data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch dropdown"
      );
    }
  }
);
